/**
 * A new message while the app is open and you're somewhere else: a slim
 * banner at the top ("Grace O'Neill · Text: Can I move to Friday?"). Tap it
 * to open the chat. Phones don't show push notifications for the app in use,
 * so this is what you see instead (Ben, 14 Oct 2026).
 *
 * Quiet when you're reading that chat, when the chat list is on screen, when
 * the quick peek is open, while notifications are paused, and for muted
 * chats (only @mentions get through, as the chat's setting says).
 *
 * Customers get it too (the client store, ClientBanner below): a salon's
 * reply while they're browsing.
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { onIncoming as salonIncoming, getState as salonState } from '../data/store';
import { clientStore } from '../data/clientStore';
import { canSeeSpace } from '../data/spaces';
import { firstName, channelLabel, cardLabel, attachmentLabel, conversationTitle } from '../data/model';
import { Avatar, Icon, prefersReducedMotion } from '../ui/bits';
import { pathForPush } from './pushRoutes';
import { hapticTick } from '../../../_utils/haptics';
import '../messages.css';
import './banner.css';

const SHOW_MS = 5000;

/** Pure apart from the page it reads: should this message get a banner? */
export function bannerWanted(m, activeId, s, where) {
    const c = s.conversations[m.conversationId];
    const meId = s.me && s.me.id;
    if (!c || !meId || m.conversationId === activeId) return false;
    if (c.space !== 'salons' && !canSeeSpace(c.space)) return false;
    const mentioned = (m.mentions || []).indexOf(meId) !== -1;
    if ((c.muted || c.notify === 'off' || c.notify === 'mentions') && !mentioned) return false;
    if (m.threadRootId && !mentioned) return false;
    const paused = s.prefs && s.prefs.pausedUntil && new Date(s.prefs.pausedUntil) > new Date();
    if (paused) return false;
    if (where.hidden || where.peekOpen) return false;
    // Already reading this chat (its id is in the address)
    if (where.path.split('/').indexOf(String(m.conversationId)) !== -1) return false;
    // In Messages the list already shows it, except on a phone reading another chat
    const base = where.base || '/messages';
    if (where.path.indexOf(base) === 0 && (where.wide || where.path.slice(base.length).split('/').filter(Boolean).length < (base === '/messages' ? 2 : 1))) return false;
    return true;
}

function describe(m, s) {
    const c = s.conversations[m.conversationId];
    const meId = s.me && s.me.id;
    const author = s.people[m.authorId] || { id: m.authorId, name: 'Someone' };
    const words = (m.text || '').replace(/\s+/g, ' ').trim() || (m.card ? cardLabel(m.card) : attachmentLabel((m.attachments || [])[0])) || 'New message';
    if (c.space === 'clients') {
        const client = s.people[c.clientId] || author;
        const via = channelLabel(m.channel || c.channel);
        const fromClient = m.authorId === c.clientId;
        return { who: client, title: `${client.name}${via ? ` · ${via}` : ''}`, body: fromClient ? words : `${firstName(author.name)}: ${words}` };
    }
    if (c.space === 'support') return { who: author, title: 'Beu Support', body: `${firstName(author.name)}: ${words}` };
    if (c.space === 'salons') {
        // A customer hearing from a salon: the salon's full name, then who wrote (unless it's the salon itself)
        const salonInfo = (s.salons && s.salons[c.salonId]) || null;
        const salonPerson = (c.memberIds || []).map((id) => s.people[id]).find((p) => p && p.kind === 'salon');
        const salon = (salonInfo && salonInfo.name) || (salonPerson && salonPerson.name) || conversationTitle(c, s.people, meId);
        const named = author.kind !== 'salon' && author.kind !== 'bailey' && author.name;
        return { who: salonPerson || author, title: salon, body: named ? `${firstName(author.name)}: ${words}` : words };
    }
    const title = conversationTitle(c, s.people, meId);
    if (c.kind === 'dm') return { who: author, title: author.name, body: words };
    return { who: author, title: c.kind === 'channel' ? `#${title}` : title, body: `${firstName(author.name)}: ${words}` };
}

export default function NewMessageBanner({ history, onIncoming = salonIncoming, getState = salonState, base = '/messages', pathFor = null }) {
    const [shown, setShown] = useState(null);   // { m, d, more }
    const [leaving, setLeaving] = useState(false);
    const timer = useRef(null);

    useEffect(() => onIncoming((m, activeId) => {
        const s = getState();
        const where = {
            path: (history && history.location && history.location.pathname) || window.location.pathname,
            wide: window.innerWidth >= 768,
            hidden: document.visibilityState === 'hidden',
            peekOpen: !!document.querySelector('.bm-peek-layer'),
            base,
        };
        if (!bannerWanted(m, activeId, s, where)) return;
        hapticTick();
        setLeaving(false);
        setShown((cur) => ({ m, d: describe(m, s), more: cur && cur.m.conversationId !== m.conversationId ? (cur.more || 0) + 1 : 0 }));
    }), [history, onIncoming, getState, base]);

    useEffect(() => {
        if (!shown) return undefined;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => close(), SHOW_MS);
        return () => clearTimeout(timer.current);
    }, [shown]); // eslint-disable-line react-hooks/exhaustive-deps

    const close = () => {
        setLeaving(true);
        setTimeout(() => { setShown(null); setLeaving(false); }, prefersReducedMotion() ? 0 : 180);
    };
    const open = () => {
        if (!shown) return;
        const c = getState().conversations[shown.m.conversationId];
        hapticTick();
        if (history && c) history.push(pathFor ? pathFor(c, shown.m) : pathForPush({ type: 'message', space: c.space, conversationId: c.id, messageId: shown.m.id }));
        close();
    };

    if (!shown) return null;
    return ReactDOM.createPortal(
        <div className={`bm-banner bm-portal${leaving ? ' is-leaving' : ''}`} role="status" aria-live="polite">
            <button type="button" className="bm-banner-open" onClick={open} aria-label={`${shown.d.title}: ${shown.d.body}. Open the chat`}>
                <Avatar person={shown.d.who} size={36} />
                <span className="bm-banner-text">
                    <b>{shown.d.title}</b>
                    <span>{shown.d.body}</span>
                    {shown.more > 0 && <small>and {shown.more} more {shown.more === 1 ? 'chat' : 'chats'}</small>}
                </span>
            </button>
            <button type="button" className="bm-banner-close" aria-label="Dismiss" onClick={close}><Icon name="close" size={16} /></button>
        </div>,
        document.body,
    );
}

/** For customers: a salon's reply while they're elsewhere in the app. */
export function ClientBanner({ history }) {
    return (
        <NewMessageBanner history={history} onIncoming={clientStore.onIncoming} getState={clientStore.getState}
            base="/dashboard/messages" pathFor={(c) => `/dashboard/messages/${encodeURIComponent(c.id)}`} />
    );
}
